import { TranslateLoader } from '@ngx-translate/core';
import { Observable, forkJoin, of, from } from 'rxjs';
import { environment } from '@env/environment';
import { HttpClient } from '@angular/common/http';
import { AzureBlobStorageService } from '@app/shared/services/api/azure-blob-storage.service';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';

@Injectable()
export class CustomTranslationsLoader implements TranslateLoader {
  private loadedLibraryTranslations: any = null;

  constructor(public http: HttpClient, private blobStorageService: AzureBlobStorageService) {}

  getTranslation(lang: string): Observable<any> {
    const fileName = 'translations.json';

    const hostTranslations$ = this.getHostTranslations(fileName);
    const libraryTranslations$ = this.getLibraryTranslations();

    return forkJoin([hostTranslations$, libraryTranslations$]).pipe(
      map(([hostTranslations, libraryTranslations]) => {
        return { ...hostTranslations, ...libraryTranslations };
      })
    );
  }

  private getHostTranslations(fileName: string): Observable<any> {
    if (window.location.hostname === 'localhost') {
      return this.http.get<any>(`/DsoStaticFiles/${environment.tenant}/${fileName}`);
    } else {
      return this.blobStorageService.getBlobFileContent(`${environment.tenant}/assets/${fileName}`);
    }
  }

  private getLibraryTranslations(): Observable<any> {
    // Check if we need to load ep-shared translations based on current route
    const needsEpSharedTranslations = this.shouldLoadEpSharedTranslations();

    if (!needsEpSharedTranslations) {
      return of({});
    }

    // Return cached translations if already loaded
    if (this.loadedLibraryTranslations) {
      return of(this.loadedLibraryTranslations);
    }

    // Use import() to dynamically load the translations synchronously
    return from(
      import('@ep-shared/reusable-components')
        .then((lib) => {
          this.loadedLibraryTranslations = lib.libLocalDe || {};
          return this.loadedLibraryTranslations;
        })
        .catch((error) => {
          // Silently handle ep-shared translation loading failure
          // This ensures the app continues to work even if the library is unavailable
          this.loadedLibraryTranslations = {};
          return {};
        })
    );
  }

  private shouldLoadEpSharedTranslations(): boolean {
    // Check if current route needs ep-shared translations
    // These are the routes that use ReusableComponentsModule
    const currentUrl = window.location.pathname;
    const routesNeedingEpShared = ['/energiedaten', '/energiedatenv2', '/sensordaten', '/meter-consumption'];

    return routesNeedingEpShared.some((route) => currentUrl.includes(route));
  }
}
